import { Input } from './input';
import { stepFor } from '@/lib/money';

export interface MoneyInputProps {
  label: string;
  currency: string;
  value: string;
  onChange: (major: string) => void;
  error?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  allowNegative?: boolean;
}

/**
 * MoneyInput holds a major-unit string; the caller converts it with toMinor
 * exactly once, on submit. Two things it does NOT do:
 *
 *   - `min={0}` does not keep a sign out of the field. Finance forms carry
 *     direction in a separate field, so the control is floored at 0 unless
 *     allowNegative — but min only makes it report itself invalid
 *     (validity.rangeUnderflow); a typed "-5" still reaches onChange and the
 *     caller has to refuse it.
 *   - it does not sanitise what it is given. `type="number"` (the house
 *     pattern for money fields) means the browser decides what reaches
 *     onChange: in jsdom "12,5" and "1 234" never arrive (the value
 *     sanitises to "") while "1e3" does, and a real browser's answer depends
 *     on its locale. Hence toMinor refuses what it cannot read exactly.
 */
export function MoneyInput({
  label,
  currency,
  value,
  onChange,
  error,
  hint,
  required,
  disabled,
  allowNegative,
}: MoneyInputProps) {
  return (
    <Input
      label={label}
      type="number"
      inputMode="decimal"
      step={stepFor(currency)}
      min={allowNegative ? undefined : 0}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      error={error}
      hint={hint ?? currency}
      required={required}
      // Input consumes `required` for the label's asterisk and does not
      // forward it to the control, so the field would not announce itself as
      // required to a screen reader. aria-required does reach the control
      // through Input's ...rest. Native validation still does not enforce it
      // — the form's own submit check has to (task-22 report).
      aria-required={required}
      disabled={disabled}
    />
  );
}
